import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2 } from "lucide-react";

/**
 * Sticky bottom bar that surfaces the "Generează N randări" CTA
 * once the user has scrolled past the in-page final-action card.
 *
 * Only renders if:
 *   - all required inputs are valid (caller passes `enabled`),
 *   - AND the targetEl (the in-page card) is below the viewport.
 *
 * On click, smooth-scrolls to the in-page button so the user sees the
 * action they're triggering, then triggers the submit handler.
 *
 * Hidden on screens wider than 640px (md+) — desktop users already
 * see the card without scrolling.
 */
export default function StickyGenerateBar({
  enabled,
  loading,
  variationCount,
  targetSelector = '[data-testid="simulate-submit-btn"]',
  onSubmit,
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    targetRef.current = document.querySelector(targetSelector);
  }, [targetSelector]);

  useEffect(() => {
    if (!enabled) {
      setShow(false);
      return;
    }
    let raf = 0;
    const check = () => {
      raf = 0;
      const el = targetRef.current || document.querySelector(targetSelector);
      if (!el) {
        setShow(false);
        return;
      }
      const rect = el.getBoundingClientRect();
      // Show when the in-page button is below the viewport (user hasn't reached it yet)
      // OR fully above the viewport (user scrolled past it).
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const below = rect.top > viewportH - 60;
      setShow(below);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled, targetSelector]);

  const handleClick = () => {
    const el = targetRef.current || document.querySelector(targetSelector);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Brief delay so the user sees the in-page button highlight before the
    // submission spinner takes over.
    setTimeout(() => onSubmit?.(), 350);
  };

  if (!show) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 inset-x-0 z-40 px-3 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-zinc-950 via-zinc-950/95 to-transparent pointer-events-none"
      data-testid="sticky-generate-bar"
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="pointer-events-auto w-full h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-2xl shadow-orange-500/40 font-medium text-base flex items-center justify-center gap-2 transition-transform active:scale-95"
        data-testid="sticky-generate-btn"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> {t("sticky.loading")}
          </>
        ) : (
          <>
            <Sparkles size={18} strokeWidth={1.5} /> {t("sticky.generate", { count: variationCount })}
            <span className="text-orange-100 text-xs ml-1">↓</span>
          </>
        )}
      </button>
    </div>
  );
}
